/**
 * Collection Page Custom JavaScript
 * 精确对标 Cowboy 网站
 * - 添加悬停购物袋图标功能
 * - 添加配送状态显示
 * - 添加自定义短标题支持
 */

class ProductCardEnhancer {
  constructor() {
    this.init();
  }

  init() {
    // 等待DOM加载完成
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.enhanceProductCards());
    } else {
      this.enhanceProductCards();
    }

    // 监听动态加载的产品（无限滚动）
    this.observeProductCards();
  }

  enhanceProductCards() {
    const productCards = document.querySelectorAll('product-card');
    
    productCards.forEach(card => {
      // 跳过已经处理过的卡片
      if (card.hasAttribute('data-enhanced')) return;
      
      this.addCartIcon(card);
      this.addStatusText(card);
      card.setAttribute('data-enhanced', 'true');
    });
  }

  addCartIcon(productCard) {
    // 检查是否已经有购物袋图标
    if (productCard.querySelector('.product-card__add-to-cart-icon')) return;

    // 获取产品信息
    const productLink = productCard.querySelector('.product-card__link');
    const productId = productCard.getAttribute('data-product-id');
    
    if (!productId) return;

    // 创建购物袋图标按钮
    const cartIconButton = document.createElement('button');
    cartIconButton.className = 'product-card__add-to-cart-icon';
    cartIconButton.setAttribute('aria-label', 'Add to cart');
    cartIconButton.setAttribute('type', 'button');
    cartIconButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
      </svg>
    `;

    // 点击事件 - 快速添加到购物车
    cartIconButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.quickAddToCart(productId, cartIconButton);
    });

    // 将图标添加到产品卡片
    productCard.insertBefore(cartIconButton, productCard.firstChild);
  }

  addStatusText(productCard) {
    // 检查是否已经添加了状态文本
    if (productCard.querySelector('.product-card__status-text')) return;

    const productContent = productCard.querySelector('.product-card__content');
    if (!productContent) return;

    // 获取产品标题元素
    const titleElement = productContent.querySelector('product-title, [class*="product-title"], h3');
    if (!titleElement) return;

    // 创建状态文本元素
    const statusText = document.createElement('div');
    statusText.className = 'product-card__status-text';
    
    // 从 data 属性读取多语言文本，支持国际化
    const deliveryText = productCard.getAttribute('data-delivery-text') || 'Delivered by Amazon';
    const outOfStockText = productCard.getAttribute('data-out-of-stock-text') || 'Temporarily out of stock';
    const isAvailable = productCard.getAttribute('data-available') !== 'false';
    
    if (isAvailable) {
      statusText.textContent = deliveryText;
    } else {
      statusText.textContent = outOfStockText;
    }

    // 在标题之前插入状态文本
    titleElement.parentNode.insertBefore(statusText, titleElement);
  }

  async quickAddToCart(productId, button) {
    // 显示加载状态
    const originalHTML = button.innerHTML;
    button.innerHTML = `
      <svg class="spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10" stroke-dasharray="31.4 31.4" stroke-linecap="round">
          <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
        </circle>
      </svg>
    `;
    button.disabled = true;

    try {
      // 获取产品的第一个可用变体
      const response = await fetch(`/products/${productId}.js`);
      const product = await response.json();
      
      if (!product || !product.variants || product.variants.length === 0) {
        throw new Error('Product not found');
      }

      // 找到第一个有库存的变体
      const availableVariant = product.variants.find(v => v.available);
      
      if (!availableVariant) {
        this.showNotification('Product is out of stock', 'error');
        button.innerHTML = originalHTML;
        button.disabled = false;
        return;
      }

      // 添加到购物车
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: availableVariant.id,
          quantity: 1
        })
      });

      if (!addResponse.ok) {
        throw new Error('Failed to add to cart');
      }

      // 显示成功消息
      this.showNotification('Added to cart!', 'success');
      
      // 更新购物车计数
      this.updateCartCount();

      // 显示成功图标
      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
        </svg>
      `;
      
      setTimeout(() => {
        button.innerHTML = originalHTML;
        button.disabled = false;
      }, 2000);

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showNotification('Failed to add to cart', 'error');
      button.innerHTML = originalHTML;
      button.disabled = false;
    }
  }

  showNotification(message, type = 'success') {
    // 移除现有通知
    const existingNotification = document.querySelector('.cart-notification');
    if (existingNotification) {
      existingNotification.remove();
    }

    // 创建通知元素
    const notification = document.createElement('div');
    notification.className = `cart-notification cart-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#000' : '#d32f2f'};
      color: #fff;
      padding: 14px 28px;
      border-radius: 8px;
      z-index: 9999;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      animation: slideIn 0.3s ease;
    `;

    // 添加动画样式
    if (!document.getElementById('notification-styles')) {
      const style = document.createElement('style');
      style.id = 'notification-styles';
      style.textContent = `
        @keyframes slideIn {
          from {
            transform: translateX(100px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100px);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(notification);

    // 3秒后移除通知
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // 更新购物车数量徽章
      const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count-bubble, .cart-count');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
        if (cart.item_count > 0) {
          element.style.display = '';
        }
      });

      // 触发购物车更新事件
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart }
      }));
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  observeProductCards() {
    // 使用 MutationObserver 监听动态添加的产品卡片
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // 元素节点
            if (node.tagName === 'PRODUCT-CARD') {
              this.addCartIcon(node);
              this.addStatusText(node);
              node.setAttribute('data-enhanced', 'true');
            } else {
              const cards = node.querySelectorAll && node.querySelectorAll('product-card:not([data-enhanced])');
              if (cards) {
                cards.forEach(card => {
                  this.addCartIcon(card);
                  this.addStatusText(card);
                  card.setAttribute('data-enhanced', 'true');
                });
              }
            }
          }
        });
      });
    });

    // 观察整个文档的变化
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// 初始化
new ProductCardEnhancer();
